import React, { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import ImageColorForm from "go-app/components/ImageForm/ImageColorForm";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CustomValidationConfig, CustomValidationConstraint, CustomValidationError } from "go-form/services/types";
import { adminApi } from "../../../../../../../../../services/Api/Admin/adminApi";
import { GoLauncherApplicationApi } from "../../../../../../../../../services/Api/Admin/adminTypes";

interface Props {
	form: UseFormReturn<GoLauncherApplicationApi>;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<GoLauncherApplicationApi>[]>>;
	application?: GoLauncherApplicationApi;
}

const GoLauncherApplicationBasicInfoForm = ({ form, setErrors, application }: Props) => {
	const { t } = useTranslation("admin");
	const {
		formState: { errors },
		register,
		watch,
		control,
	} = form;
	const textInputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;
	const numberAndSelectInputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const searchCategory = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return adminApi.getGoLauncherApplicationsSearchSelect(search, params, options);
	};

	const searchType = async (search: string, _: Record<string, any>, options?: Record<string, any>) => {
		const res = await adminApi.getGoLauncherApplicationTypes(search, options);
		return res.map((item) => ({
			value: item,
			label: item,
		}));
	};

	const searchSupportedVersions = async (search: string, _: Record<string, any>, options?: Record<string, any>) => {
		const res = await adminApi.getGoLauncherApplicationSupportedVersions(search, options);
		return res.map((item) => ({
			value: item,
			label: item,
		}));
	};

	return (
		<>
			<h5>{t("lib:common.word.basic_data")}</h5>
			<fieldset className="form-group">
				<div className="row">
					<div className="col-md-10">
						<FormInput
							name="name"
							errors={errors}
							register={register}
							label={t("lib:common.word.name")}
							customValidationConfig={textInputValidationConfig}
						/>
						<FormInput
							name="description"
							errors={errors}
							register={register}
							label={t("lib:common.word.description")}
							as="textarea"
						/>
						<FormSelectGroup
							errors={errors}
							control={control}
							loadOptions={searchCategory}
							label={t("common.word.category")}
							getOptionValue={(option) => option.id}
							name="category_id"
							customValidationConfig={numberAndSelectInputValidationConfig}
							data-testid="category_id"
							defaultValue={{
								label: watch("category")?.name,
								id: watch("category")?.id,
							}}
						/>
						<FormSelectGroup
							errors={errors}
							control={control}
							loadOptions={searchType}
							label={t("lib:common.word.type")}
							name="type"
							customValidationConfig={numberAndSelectInputValidationConfig}
							data-testid="type"
							defaultValue={{
								label: watch("type"),
								value: watch("type"),
							}}
						/>
						<FormInput
							errors={errors}
							name="package_id"
							label={t("modules.go_launcher.field.package_id.title")}
							register={register}
							customValidationConfig={textInputValidationConfig}
						/>
						<FormNumberInput
							errors={errors}
							name="position"
							label={t("modules.go_launcher.field.position.title")}
							control={control}
							suffix=""
							allowNegative={false}
							customValidationConfig={numberAndSelectInputValidationConfig}
						/>
						<FormSelectGroup
							errors={errors}
							control={control}
							loadOptions={searchSupportedVersions}
							label={t("modules.go_launcher.field.supported_versions.title")}
							name="supported_versions"
							isMulti
							data-testid="supported_versions"
							defaultValue={watch("supported_versions")?.map((value) => ({
								value,
								label: value,
							}))}
						/>
					</div>
					<div className="col-md-2" style={{ minWidth: "150px" }}>
						<ImageColorForm
							form={form}
							handleSave={() => {}}
							prefix={"image"}
							mode="IMAGE"
							data={{ ...application, color: "" }}
							resourceLink={application?.icon}
							label={`${t("modules.go_launcher.field.icon.title")} *`}
							shouldOverrideImageLink
						/>
					</div>
				</div>
			</fieldset>
		</>
	);
};

export default GoLauncherApplicationBasicInfoForm;
